import React, { lazy, Suspense, useId, useRef, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { ClientOnly } from '@tanstack/react-router';
import { Tooltip } from 'react-tooltip';
import styles from './EntryCard.module.scss';
import ToolTipIcon from '../ToolTip/ToolTip';
import PppnsTooltip from '../PppnsTooltip/PppnsTooltip';
import { getIsVerified } from '../../../helpers';
import EyeIcon from '../../../images/icons/eye.svg';

const LazyLeafletMap = lazy(() => import('../Maps/LeafletMap'));
import MapSkeleton from '../Maps/MapSkeleton';

/*
This is the component used for property entries, it displays different data from the SafariInner.
*/

function PropertyEntryCard({
  entry,
  onCompareToggle,
  isCompareSelected,
  isCompareLimitReached,
  onOpenPlanner,
}) {
  const navigate = useNavigate();
  const [showMap, setShowMap] = useState(false);
  const closeTimerRef = useRef(null);
  const descriptionTooltipId = useId();

  const openMap = () => {
    clearTimeout(closeTimerRef.current);
    setShowMap(true);
  };
  const scheduleCloseMap = () => {
    clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setShowMap(false), 150);
  };

  if (!entry) return null;
  const prefix = '/accommodation';
  const isVerified = getIsVerified(entry?.owner);

  const locationParts = [
    entry.visibleOn?.destinationArea?.name,
    entry.visibleOn?.destination?.name,
  ].filter(Boolean);

  const countryName =
    entry.country?.name || entry.visibleOn?.destination?.country?.name;

  const firstPhoto = entry?.photos?.find((p) => p && p.active !== false);

  return (
    <div className={styles['property-card']}>
      <Link to={`${prefix}/${entry?.slug}`}>
        <div className={styles['property-media']}>
          <img
            src={firstPhoto?.url}
            alt={firstPhoto?.title || entry?.title}
            loading="lazy"
            decoding="async"
          />

          {entry.propertyType?.name && (
            <span className={styles['property-badge']}>
              {entry.propertyType.name}
            </span>
          )}

          {isVerified && (
            <div className={styles['property-verified']}>
              <ToolTipIcon isSmall={true} />
            </div>
          )}
        </div>
      </Link>

      <div className={styles['property-body']}>
        <div className={styles['property-title-row']}>
          <h3 className={styles['property-title']}>
            <Link to={`${prefix}/${entry?.slug}`} className={styles['property-title-link']}>
              {entry?.title}
            </Link>
          </h3>
          {entry?.briefDescription && (
            <>
              <span
                className={styles['property-info']}
                data-tooltip-id={`property-description-${descriptionTooltipId}`}
                data-tooltip-content={entry.briefDescription}
                aria-label="Property description"
              >
                i
              </span>
              <Tooltip
                id={`property-description-${descriptionTooltipId}`}
                className={styles['property-info-tooltip']}
                place="top"
              />
            </>
          )}
        </div>
        <p className={styles['property-location']}>
          {[...locationParts, countryName].filter(Boolean).join(', ')}
        </p>

        <div className={styles['property-meta']}>
          {typeof entry?.rooms === 'number' && (
            <span className={styles['property-chip']}>
              {entry.rooms} {entry.rooms === 1 ? 'room' : 'rooms'}
            </span>
          )}
          <span
            className={`${styles['property-chip']} ${styles['property-views']}`}
          >
            <img src={EyeIcon} alt="views" />
            {entry?.views || 0} views
          </span>
        </div>

        <div className={styles['property-foot']}>
          <div className={styles['property-price']}>
            <span>from</span> <b>${entry?.costPerPersonFrom}</b> <PppnsTooltip text="pppns, low season" />
            {entry?.costPerPersonTo && (
              <span className={styles['property-price-high']}>
                {' '}
                · up to ${entry.costPerPersonTo} high season
              </span>
            )}
          </div>
          <div className={styles['property-actions']}>
            <button
              type="button"
              className={styles['btn-enquire']}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (onOpenPlanner) {
                  onOpenPlanner({ property: entry, intent: 'quotes' });
                } else {
                  navigate({
                    to: `${prefix}/${entry?.slug}`,
                    search: { intent: 'quotes' },
                  });
                }
              }}
            >
              Book Now
            </button>
          </div>
        </div>

        <div className={styles['property-bottom-row']}>
          <button
            type="button"
            className={`${styles['btn-compare']} ${
              isCompareSelected ? styles['btn-compare--selected'] : ''
            }`}
            onClick={() => onCompareToggle?.()}
            disabled={!isCompareSelected && isCompareLimitReached}
          >
            {isCompareSelected ? '✓ Added' : 'Compare'}
          </button>

          {entry.location?.latitude && entry.location?.longitude && (
            <div
              className={styles['property-map-toggle']}
              onMouseEnter={openMap}
              onMouseLeave={scheduleCloseMap}
            >
              <button
                type="button"
                className={styles['property-map-trigger']}
                onClick={(e) => {
                  e.preventDefault();
                  setShowMap((prev) => !prev);
                }}
                aria-expanded={showMap}
              >
                View on map
              </button>
              {showMap && (
                <div
                  className={styles['property-map-popup']}
                  onMouseEnter={openMap}
                  onMouseLeave={scheduleCloseMap}
                >
                  <div className={styles.mapWrap}>
                    <ClientOnly
                      fallback={<MapSkeleton label="Loading property map…" />}
                    >
                      <Suspense
                        fallback={<MapSkeleton label="Initializing map…" />}
                      >
                        <LazyLeafletMap
                          longitude={entry.location?.longitude}
                          latitude={entry.location?.latitude}
                          zoom={8}
                        />
                      </Suspense>
                    </ClientOnly>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PropertyEntryCard;
